/**
 * use-relance-draft — Flux partage « Juliette redige le mail de relance ».
 *
 * Centralise la pre-redaction en 1 clic (ouverte depuis un signal OU depuis la
 * carte « Juliette recommande » de la fiche) : ouvre le PendingEmailDialog en
 * loading, invoque generate-relance-email, pose le pending_email_id puis montre
 * l'editeur de validation. Gere l'annulation (si l'agent ferme le dialog pendant
 * la generation, on ignore la reponse tardive) et les erreurs.
 *
 * `busyKey` permet a l'appelant de savoir QUELLE gachette est en cours (afficher
 * un spinner sur la bonne ligne / le bon bouton) : passer un identifiant stable
 * (id du signal, id du contact…).
 */

import { useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { extractFunctionError } from "@/lib/supabase-fn-error";

interface StartDraftArgs {
  /** Identifiant de la gachette (signal.id, contact.id…) pour tracer le busy. */
  key: string;
  contactId: string;
  /** Instructions de ton calibrees par contexte (relanceInstructionsForSignal). */
  instructions?: string;
  contactName?: string;
  /** Callback succes (ex : marquer le signal vu + invalider le cache). */
  onDrafted?: () => void;
}

interface DialogState {
  open: boolean;
  pendingId: string | null;
  contactName?: string;
}

export function useRelanceDraft() {
  const { toast } = useToast();
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [dialog, setDialog] = useState<DialogState>({ open: false, pendingId: null });
  // Gachette dont la generation est en vol. Si l'agent ferme le dialog entre-temps,
  // on remet a null pour IGNORER la reponse tardive (pas de reouverture surprise).
  const activeRef = useRef<string | null>(null);

  const startDraft = async (args: StartDraftArgs) => {
    // Garde synchrone anti-double-clic : une generation deja en vol bloque toute
    // nouvelle (la ref est synchrone, contrairement a setBusyKey qui est async).
    if (activeRef.current) return;
    setBusyKey(args.key);
    activeRef.current = args.key;
    setDialog({ open: true, pendingId: null, contactName: args.contactName });
    try {
      const { data, error } = await supabase.functions.invoke("generate-relance-email", {
        body: {
          contact_id: args.contactId,
          ...(args.instructions ? { instructions: args.instructions } : {}),
        },
      });
      if (error) throw error;
      // L'agent a ferme le dialog pendant la generation → on ignore la reponse.
      if (activeRef.current !== args.key) return;
      const pendingId = (data as { data?: { pending_email_id?: string } })?.data?.pending_email_id;
      if (!pendingId) throw new Error("Réponse inattendue (pas de pending_email_id)");
      setDialog({ open: true, pendingId, contactName: args.contactName });
      args.onDrafted?.();
    } catch (err) {
      if (activeRef.current === args.key) {
        setDialog({ open: false, pendingId: null });
        toast({
          title: "Préparation impossible",
          description: await extractFunctionError(err),
          variant: "destructive",
        });
      }
    } finally {
      if (activeRef.current === args.key) activeRef.current = null;
      setBusyKey((cur) => (cur === args.key ? null : cur));
    }
  };

  /** Props a spreader sur <PendingEmailDialog {...dialogProps} />. */
  const dialogProps = {
    open: dialog.open,
    onOpenChange: (o: boolean) => {
      if (!o) activeRef.current = null; // fermeture → ignore une generation en vol
      setDialog((d) => ({ ...d, open: o }));
    },
    pendingId: dialog.pendingId,
    contactName: dialog.contactName,
  };

  return { startDraft, dialogProps, busyKey };
}
